// src/features/records/hooks/useRecordDraft.js

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const withDateFallback = (originalValue) => {
  const converted = toDate(originalValue);
  return converted ?? originalValue;
};

const makeNotFoundError = () => {
  const err = new Error('Draft not found');
  err.code = 'not-found';
  return err;
};

/**
 * Normalize draft data while preserving unknown fields.
 * Converts some known timestamp-ish fields to Date when possible.
 */
const serializeDraft = (id, data) => {
  if (!data) return null;

  const content = data.content ?? {};
  const attributes = data.attributes ?? {};
  const links = data.links ?? { outgoing: [] };
  const metadata = data.metadata;

  const nextAttributes = {
    ...attributes,
  };

  if (attributes.date && typeof attributes.date === 'object') {
    nextAttributes.date = {
      ...attributes.date,
      start: withDateFallback(attributes.date.start),
      end: withDateFallback(attributes.date.end),
    };
  }

  if (attributes.dueDate !== undefined) {
    nextAttributes.dueDate = withDateFallback(attributes.dueDate);
  }

  const nextMetadata =
    metadata && typeof metadata === 'object'
      ? {
          ...metadata,
          ...(metadata.createdAt !== undefined && {
            createdAt: withDateFallback(metadata.createdAt),
          }),
          ...(metadata.expireAt !== undefined && {
            expireAt: withDateFallback(metadata.expireAt),
          }),
        }
      : metadata;

  return {
    id,
    ...data,
    content,
    attributes: nextAttributes,
    links,
    metadata: nextMetadata,
  };
};

/**
 * Subscribe to a record draft document in Firestore.
 * @param {string|null|undefined} draftId
 * @returns {{ draft: Object|null, loading: boolean, error: Error|null }}
 */
export default function useRecordDraft(draftId) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(Boolean(draftId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!draftId) {
      setDraft(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    setDraft(null);

    const ref = doc(db, 'recordDrafts', draftId);

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setDraft(null);
          setError(makeNotFoundError());
        } else {
          setDraft(serializeDraft(snap.id, snap.data()));
          setError(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load record draft:', err);
        setDraft(null);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [draftId]);

  return { draft, loading, error };
}
